import { keepPreviousData, useQuery } from "@tanstack/react-query";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import { toCamelKeyObject } from "@/utils/case";
import { pathPickQueryApi } from "@/features/pathpick/api/query";
import { PathPickProductRequestParams } from "@/features/pathpick/types/request-params";
import { PathPickProductIdsResponse } from "@/features/pathpick/types/response";

// 전체 선택용 id 조회 — 조회 조건이 바뀌면 미리 받아 "전체 n개 선택"의 n으로 쓰고,
// 누르면 받아 둔 ids를 그대로 선택한다(보여준 n과 실제 선택 수가 같도록 재조회하지 않는다).
// ids_only는 최대 1만 건까지만 돌려주므로 n도 1만에서 멈춘다.
// 키를 목록(api-products)과 접두사부터 나눈 이유: 담기의 낙관적 업데이트(setQueriesData)와
// 각 mutation의 invalidate가 그 접두사 아래를 전부 목록 응답으로 보고 다루는데,
// 모양이 다른 id 응답이 섞이면 그 전제가 깨진다. 대신 mutation마다 이 키도 함께 invalidate한다.
// params의 페이지 정보는 무시한다 — 목록 조회 params를 그대로 넘기면 되고, 페이지 이동에는 다시 받지 않는다.
const usePathPickAllResultIds = (
  { pageNumber, pageSize, ...params }: PathPickProductRequestParams,
  { enabled }: { enabled: boolean }
) => {
  const { companyId } = useCompanyIdentity();

  const { data, isFetching } = useQuery({
    queryKey: ["pathpick", "api-product-ids", companyId, params],
    queryFn: async () =>
      toCamelKeyObject(
        await pathPickQueryApi.getPathPickProductIds(
          companyId as number,
          params
        )
      ) as PathPickProductIdsResponse,
    enabled: Boolean(companyId) && enabled,
    // 조건이 바뀌는 동안 이전 n을 유지해 체크박스가 사라졌다 나타나지 않게 한다.
    placeholderData: keepPreviousData,
    // 백그라운드 조회라 실패하면 알리지 않고 전체 선택만 숨긴다 — 재시도 동안 스피너가 남지 않게.
    retry: false,
  });

  const resultIds = data?.products ?? null;
  // totalCount는 자르기 전 전체 수라, 받은 개수보다 크면 상한(1만 건)에서 잘린 것이다.
  // truncated 필드는 스키마에 없어 이 비교로 판정한다.
  const isTruncated = data ? data.totalCount > data.products.length : false;

  return { resultIds, isTruncated, isFetching };
};

export default usePathPickAllResultIds;
